import { useEffect } from "react";

const PREWARM_DELAY_MS = 400;
// Event pages live at the edge for s-maxage=30 + stale-while-revalidate=120 (app/server.ts),
// so a page warmed at dialog-open is cold again after 150s of the user writing their post.
const EDGE_WINDOW_MS = 120_000;

const warmedAt = new Map<string, number>();

const wantsLessData = (): boolean =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    ?.saveData === true;

// X crawls the link while the post is being composed, and a cold SSR render takes longer than
// its fetch timeout — so the first share of a link arrives without a card. Requesting the exact
// url while the dialog is open leaves it warm in the CDN before the crawler asks for it.
export const usePrewarmSharePage = (url: string): void => {
  useEffect(() => {
    if (!url || wantsLessData()) {
      return;
    }

    const isWarm = () => Date.now() - (warmedAt.get(url) ?? 0) < EDGE_WINDOW_MS;

    const warm = () => {
      warmedAt.set(url, Date.now());
      void fetch(url, { credentials: "omit", cache: "no-store" }).catch(
        () => {}
      );
    };

    const debounce = window.setTimeout(() => {
      if (!isWarm()) {
        warm();
      }
    }, PREWARM_DELAY_MS);
    const keepWarm = window.setInterval(warm, EDGE_WINDOW_MS);

    return () => {
      window.clearTimeout(debounce);
      window.clearInterval(keepWarm);
    };
  }, [url]);
};
